import { Controller } from "@hotwired/stimulus";
import { sanitizeCreditCard } from "../sanitizers";

/**
 * Sanitizes credit card numbers from input fields.
 *
 * NOTE: There is a server-side equivalent in app/models/concerns/sanitize_credit_card_numbers.rb.
 * If you change the sanitization logic here, update it there too.
 *
 * Usage (directly on input/textarea):
 *   <textarea data-controller="credit-card-sanitizer"
 *             data-action="focusout->credit-card-sanitizer#sanitize"></textarea>
 *
 * Or wrapping a field:
 *   <div data-controller="credit-card-sanitizer">
 *     <input data-action="focusout->credit-card-sanitizer#sanitize" name="first_name" />
 *     <textarea data-action="focusout->credit-card-sanitizer#sanitize" name="comments"></textarea>
 *   </div>
 *
 * On a form whose submission would otherwise carry an unsanitized value -- a GET form, where the
 * value lands in the URL -- also sanitize on submit, since focusout never fires when the field is
 * still focused as Enter submits:
 *   <form data-action="submit->credit-card-sanitizer#sanitizeAll">
 */
export default class extends Controller {
  // Every field this controller sanitizes on focusout, swept before the form serializes. Enter from a
  // focused input submits without ever firing focusout, so a per-field handler alone leaves the raw
  // value to be submitted.
  sanitizeAll() {
    this.element
      .querySelectorAll("[data-action*='credit-card-sanitizer#sanitize']")
      .forEach((element) => this.sanitize({ target: element }));
  }

  sanitize(event) {
    const element = event.target;
    const originalValue = element.value;
    const sanitizedValue = sanitizeCreditCard(originalValue);

    if (sanitizedValue !== originalValue) {
      element.value = sanitizedValue;
      element.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
}
